import React from "react";
import { Flex, Form, Select } from "antd";
import { observer } from "mobx-react-lite";
import { AttrTypeName } from "src/types/AttrType";
import { classNames } from "src/common/classNames";
import { makeFieldOptions } from "src/common/makeFieldOptions";
import { NamePath } from "antd/es/form/interface";
import { PathType } from "src/types/PathType";
import styles from "./FormObjectSection.module.less";
import { FormObjectSectionsStore } from "./FormObjectSectionsStore";

interface AttrDef<TFields extends {}> {
  attrKey: PathType<TFields>;
  attrLabel: string;
  attrType?: AttrTypeName | AttrTypeName[];
  required?: boolean;
  multiple?: boolean;
}

interface PropsFormObjectSection<TFields extends {}, TObjType extends string> {
  objKey: PathType<TFields>;
  objType: TObjType;
  store: FormObjectSectionsStore<TObjType>;
  attrs?: AttrDef<TFields>[];
  objLabel?: string;
  title?: string;
  attrTitle?: string;
  isObjSelect?: boolean;
  required?: boolean;
}

export const FormObjectSection = observer(
  <TFields extends {}, TObjType extends string>({
    store,
    title,
    attrTitle,
    objKey,
    objType,
    objLabel,
    attrs,
    isObjSelect = true,
    required = true,
  }: PropsFormObjectSection<TFields, TObjType>) => {
    const { objects, attrsMap, loadingAttrs } = store;
    const form = Form.useFormInstance();
    const fieldName = (name: PathType<TFields>) => name as NamePath<TFields>;

    const idObj = Form.useWatch(objKey);
    React.useEffect(() => {
      if (idObj && attrs?.length) store.loadAttrs(objType, idObj);
    }, [idObj]);

    const handleChange = () =>
      attrs?.forEach(({ attrKey }) => form.setFieldValue(attrKey, undefined));

    const makeAttrOptions = (attrType?: AttrTypeName | AttrTypeName[]) => {
      const attributes = attrsMap[objType] ?? [];
      if (!attrType) return makeFieldOptions(attributes);
      if (Array.isArray(attrType)) {
        return attrType.flatMap((type) =>
          store.makeOptionsByType(type, attributes),
        );
      }
      return store.makeOptionsByType(attrType, attributes);
    };

    return (
      <Flex gap={12} vertical>
        {title && <div className={styles.title}>{title}</div>}
        {isObjSelect && (
          <Form.Item
            name={fieldName(objKey)}
            label={objLabel}
            rules={[{ required }]}
          >
            <Select
              options={makeFieldOptions(objects)}
              showSearch
              optionFilterProp="filterProp"
              allowClear
              onChange={handleChange}
              placeholder="Выбрать"
            />
          </Form.Item>
        )}
        {!!attrs?.length && (
          <>
            {attrTitle && <div>{attrTitle}</div>}
            <div className={classNames([[attrs.length > 1, styles.twoCols]])}>
              {attrs.map(
                ({
                  attrKey,
                  attrLabel,
                  attrType,
                  required: attrRequired = true,
                  multiple,
                }) => (
                  <Form.Item
                    key={attrKey.join("-")}
                    name={fieldName(attrKey)}
                    label={attrLabel}
                    rules={[{ required: attrRequired }]}
                  >
                    <Select
                      options={makeAttrOptions(attrType)}
                      showSearch
                      optionFilterProp="filterProp"
                      allowClear
                      loading={loadingAttrs.has(objType)}
                      disabled={!idObj}
                      placeholder="Выбрать"
                      mode={multiple ? "multiple" : undefined}
                    />
                  </Form.Item>
                ),
              )}
            </div>
          </>
        )}
      </Flex>
    );
  },
);
